import { useMemo, useState } from "react";

import { cn } from "@/lib/utils";
import { Spinner } from "@fluentui/react-components";
import { Funnel, Search } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import ApprovedCandidatesTable from "./approved-candidates-table";
import ApprovedCandidateDrawer from "./approved-candidate-drawer";

import { useAdminApprovedCandidatesQuery } from "@/queries/admin/approved-candidates";

import { AdminPipelineCandidate } from "@/queries/admin/candidate-pipeline";
import { roleList } from "@/redux/features/candidateProfileSlice";

const ApprovedCandidates = () => {
  const [selectedCandidate, setSelectedCandidate] =
    useState<AdminPipelineCandidate>();
  const [searchString, setSearchString] = useState("");
  const [filterFn, setFilterFn] = useState<
    (c: AdminPipelineCandidate) => boolean
    // Lazy initialization
  >(() => () => true);

  const { adminApprovedCandidates, adminApprovedCandidatesLoading } =
    useAdminApprovedCandidatesQuery();

  const candidates = useMemo(() => {
    if (searchString) {
      const filterString = searchString.toLowerCase();
      return adminApprovedCandidates?.filter(
        (candidate) =>
          candidate.name?.toLowerCase().includes(filterString) &&
          filterFn(candidate),
      );
    } else {
      return adminApprovedCandidates?.filter(filterFn);
    }
  }, [adminApprovedCandidates, searchString, filterFn]);

  const count = useMemo(() => candidates?.length, [candidates]);

  if (adminApprovedCandidatesLoading) {
    return (
      <div>
        <h3 className="py-4 text-2xl font-semibold group-hover:underline text-left">
          Candidates
        </h3>
        <div className="w-full flex-col justify-around align-middle">
          <Spinner size="huge" />
          <p className="text-center text-base font-medium text-[#727E94]">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3 className="py-4 text-2xl font-semibold group-hover:underline text-left">
        Candidates
      </h3>
      <div className="w-full flex flex-col gap-4 items-start self-stretch">
        <div className="flex flex-col md:flex-row md:items-center justify-between self-stretch">
          <p className="text-lg text-[#181d27] font-semibold">
            {`${count} ${count === 1 ? "Candidate" : "Candidates"}`}
          </p>
          <div className="flex flex-row items-center gap-3">
            <Filter onChange={(fn) => setFilterFn(() => fn)} />
            <div
              className={cn(
                "flex flex-row items-center gap-0.5",
                "border-b border-[#57576B] transition-colors focus-within:border-black",
              )}
            >
              <Search />
              <input
                type="text"
                className={cn(
                  "flex h-10 w-full min-w-48 px-3 py-2 text-base",
                  "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
                )}
                placeholder="Search for any candidate"
                value={searchString}
                onChange={(ev) => {
                  ev.preventDefault();
                  setSearchString(ev.target.value);
                }}
              />
            </div>
          </div>
        </div>
        <ApprovedCandidatesTable
          candidates={candidates}
          setSelectedCandidate={setSelectedCandidate}
        />
      </div>
      <ApprovedCandidateDrawer
        candidate={selectedCandidate}
        setOpen={() => setSelectedCandidate(undefined)}
      />
    </div>
  );
};

const ROLE_OPTIONS = roleList;

type FilterField = "role";
type FilterCondition = "is" | "is not" | "includes" | "excludes";
type FilterValue = string | string[];

interface Filter {
  field?: FilterField;
  condition?: FilterCondition;
  value?: FilterValue;
}

const isMultiValueCondition = (condition: FilterCondition) =>
  condition === "includes" || condition === "excludes";

const isFilterActive = (filter: Filter) => {
  // Not active if a field is missing
  if (!filter.field || !filter.condition || !filter.value) {
    return false;
  }

  // If the condition accepts multiple values, check that our array has content
  if (isMultiValueCondition(filter.condition)) {
    return Array.isArray(filter.value) && filter.value.length > 0;
  }

  // For non-multivalue, all fields are set
  return true;
};

const DEFAULT_FILTER: Filter = {
  field: "role",
  condition: "is",
  value: undefined,
};

const Filter = ({
  onChange,
}: {
  onChange: (fn: (candidate: AdminPipelineCandidate) => boolean) => void;
}) => {
  const [filter, setFilter] = useState<Filter>({ ...DEFAULT_FILTER });
  const [filterApplied, setFilterApplied] = useState(false);

  const filterActive = isFilterActive(filter);

  const handleValueChange = (newValue: FilterValue) => {
    setFilter((prev) => ({
      ...prev,
      value: newValue,
    }));
  };

  const handleClear = () => {
    setFilter({ ...DEFAULT_FILTER });
    onChange(() => true);
    setFilterApplied(false);
  };

  const handleApply = () => {
    const { field, condition, value } = filter;

    // Early exit: incomplete or empty filter
    if (
      !field ||
      !condition ||
      value === undefined ||
      (Array.isArray(value) && value.length === 0)
    ) {
      onChange(() => true);
      setFilterApplied(false);
      return;
    }

    // Compile optimized filter function
    let fn: (candidate: AdminPipelineCandidate) => boolean;

    if (condition === "is") {
      fn = (candidate) => candidate[field] === value;
    } else if (condition === "is not") {
      fn = (candidate) => candidate[field] !== value;
    } else if (
      (condition === "includes" || condition === "excludes") &&
      Array.isArray(value)
    ) {
      if (condition === "includes") {
        fn = (candidate) => {
          const candidateValue = candidate[field];
          return !!candidateValue && value.includes(candidateValue);
        };
      } else {
        fn = (candidate) => {
          const candidateValue = candidate[field];
          return !!candidateValue && !value.includes(candidateValue);
        };
      }
    } else {
      // Fallback to "allow all" if something is weird
      fn = () => true;
    }

    onChange(fn);
    setFilterApplied(true);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline">
          <Funnel />
          {filterApplied ? "Filter (1)" : "Filter"}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-2 flex flex-col gap-2 rounded-md border bg-white"
        align="center"
      >
        <div className="flex flex-row gap-4 items-center">
          <div className="text-base">Role</div>
          <div className="text-base">is</div>
          {filter.condition ? (
            isMultiValueCondition(filter.condition) ? undefined : (
              <Select
                // Radix Select doesn't re-render when value switches no undefined
                // So we add a key that changes to force a re-render
                key={typeof filter.value === "string" ? filter.value : "empty"}
                value={
                  typeof filter.value === "string" ? filter.value : undefined
                }
                onValueChange={(val) => handleValueChange(val)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((role) => (
                    <SelectItem key={role} value={role}>
                      {role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )
          ) : undefined}
        </div>
        {filterActive && (
          <div className="flex justify-between pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="text-xs text-muted-foreground"
            >
              Clear
            </Button>
            <Button variant="default" size="sm" onClick={handleApply}>
              Apply
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default ApprovedCandidates;
